import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import CTAFooter from './CTAFooter';

export default function LegalPageLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative">
      {/* Cross/Plus grid pattern overlay */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      
      {/* Content */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10"
      >
        {/* Back link */}
        <div className="max-w-[800px] mx-auto px-6 sm:px-8 pt-8 sm:pt-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[13px] text-white/40 hover:text-white/70 transition-colors"
          >
            <ArrowLeft size={14} />
            aibl.money
          </Link>
        </div>

        {/* Page header */}
        <div className="max-w-[800px] mx-auto px-6 sm:px-8 pt-12 sm:pt-20 pb-12 sm:pb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[36px] sm:text-[48px] font-bold text-white tracking-[-0.02em]"
            style={{ fontFamily: "'RC Demo', monospace, system-ui" }}
          >
            {title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[13px] text-white/35 mt-4"
          >
            Last updated: {lastUpdated}
          </motion.p>
        </div>

        {/* Legal content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-[800px] mx-auto px-6 sm:px-8 pb-24 sm:pb-32"
        >
          <div className="legal-content">
            {children}
          </div>
        </motion.div>

        {/* Footer */}
        <CTAFooter />
      </motion.div>

      {/* Styles for legal content */}
      <style>{`
        .legal-content h2 {
          font-size: 20px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 48px;
          margin-bottom: 20px;
          padding-top: 48px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .legal-content h2:first-child {
          margin-top: 0;
          padding-top: 0;
          border-top: none;
        }
        
        .legal-content .section-num {
          font-size: 14px;
          color: rgba(255,255,255,0.3);
          font-family: monospace;
        }
        
        .legal-content h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin-top: 28px;
          margin-bottom: 12px;
        }
        
        .legal-content p {
          font-size: 15px;
          line-height: 1.8;
          color: rgba(255,255,255,0.7);
          margin-bottom: 16px;
        }
        
        .legal-content ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 16px;
        }
        
        .legal-content ul li {
          font-size: 15px;
          line-height: 1.8;
          color: rgba(255,255,255,0.7);
          padding-left: 20px;
          position: relative;
          margin-bottom: 8px;
        }
        
        .legal-content ul li::before {
          content: "–";
          position: absolute;
          left: 0;
          color: rgba(255,255,255,0.3);
        }
        
        .legal-content strong {
          color: white;
          font-weight: 600;
        }
        
        .legal-content .caps-warning {
          font-size: 14px;
          line-height: 1.7;
          color: rgba(255,255,255,0.6);
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 8px;
          padding: 16px 20px;
          margin: 20px 0;
        }
        
        .legal-content a {
          color: white;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        
        .legal-content a:hover {
          color: rgba(255,255,255,0.7);
        }
      `}</style>
    </div>
  );
}
